import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  List,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
  Badge,
  Button,
  Autocomplete,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Send,
  MoreVert,
  AttachFile,
  InsertEmoticon,
  Search,
  Delete,
  Forward,
  Share,
  Forum,
} from '@mui/icons-material';
import moment from 'moment';
import EmojiPicker from 'emoji-picker-react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../Components/Header';

export default function LMSMessages() {
  const theme = useTheme();
  const [recipientInput, setRecipientInput] = useState('');
  const [recipientList, setRecipientList] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [recentChats, setRecentChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAttachmentDialog, setShowAttachmentDialog] = useState(false);
  const [fileInput, setFileInput] = useState(null);
  const [user, setUser] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const scrollRef = useRef(null);
  const token = localStorage.getItem('jwttoken');

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const [msgRes, userRes] = await Promise.all([
          axios.get('https://tdtlworld.com/dss-kaas-backend/socialapp/messages/', {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get('https://tdtlworld.com/dss-kaas-backend/socialapp/users/', {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const { user: currentUser, messages: fetchedMessages } = msgRes.data;
        setUser(currentUser);
        setRecipientList(userRes.data);

        const updatedMessages = fetchedMessages.map((msg) => ({
          ...msg,
          id: msg.id,
          sender: msg.sender,
          receiver: msg.receiver,
          content: msg.content,
          timestamp: msg.timestamp,
        }));
        setMessages(updatedMessages);

        // Extract recent chats
        const chatsMap = new Map();
        updatedMessages.forEach((msg) => {
          const chatKey = msg.sender === `${currentUser.first_name} ${currentUser.last_name}` ? msg.receiver : msg.sender;
          if (!chatsMap.has(chatKey)) {
            const senderUser = userRes.data.find(u => `${u.first_name} ${u.last_name}` === chatKey);
            chatsMap.set(chatKey, {
              name: chatKey,
              message: msg.content,
              time: msg.timestamp,
              user: senderUser?.user_id || null,
              first_name: senderUser?.first_name || chatKey,
              last_name: senderUser?.last_name || '',
            });
          }
        });
        setRecentChats(Array.from(chatsMap.values()));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, [token]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, selectedChat]);

  const handleChatClick = (chat) => {
    setSelectedChat(chat.user || chat.name);
    setSelectedRecipients([{ user_id: chat.user, first_name: chat.first_name, last_name: chat.last_name }]);
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || selectedRecipients.length === 0) return;

    const newMessage = {
      receiver_id: selectedRecipients.length === 1 ? selectedRecipients[0].user_id : selectedRecipients.map(r => r.user_id),
      content: messageInput.trim(),
    };

    try {
      const response = await axios.post(
        'https://tdtlworld.com/dss-kaas-backend/socialapp/messages/send/',
        newMessage,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const sentMsg = {
        id: response.data.id,
        sender: `${user.first_name} ${user.last_name}`,
        receiver: selectedRecipients.map(r => `${r.first_name} ${r.last_name}`).join(', '),
        content: messageInput.trim(),
        timestamp: new Date().toISOString(),
        is_read: false,
      };

      setMessages(prev => [...prev, sentMsg]);
      setMessageInput('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const addEmoji = (emojiData) => {
    setMessageInput(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const handleMessageMenuOpen = (event, msg) => {
    setAnchorEl(event.currentTarget);
    setSelectedMessage(msg);
  };

  const handleMessageMenuClose = () => {
    setAnchorEl(null);
    setSelectedMessage(null);
  };

  const handleDeleteMessage = () => {
    if (selectedMessage) {
      setMessages(prev => prev.map(m => m.id === selectedMessage.id ? { ...m, is_deleted: true } : m));
    }
    handleMessageMenuClose();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
      <Header />
      
      <Box sx={{ display: 'flex', flexGrow: 1, mt: '64px', overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box sx={{ 
          width: { xs: 80, md: 320 }, 
          borderRight: '1px solid', 
          borderColor: 'divider', 
          display: 'flex', 
          flexDirection: 'column',
          bgcolor: 'background.paper'
        }}>
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" fontWeight={800} sx={{ display: { xs: 'none', md: 'block' } }}>Messages</Typography>
            <IconButton color="primary">
              <MoreVert />
            </IconButton>
          </Box>
          
          <Box sx={{ p: 2, display: { xs: 'none', md: 'block' } }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search conversations..."
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                sx: { borderRadius: '50px' }
              }}
            />
          </Box>

          <List sx={{ flexGrow: 1, overflowY: 'auto', p: 0 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ px: 3, py: 1, display: { xs: 'none', md: 'block' } }}>RECENT CHATS</Typography>
            {recentChats.map((chat, idx) => (
              <ListItemButton 
                key={idx} 
                selected={selectedChat === (chat.user || chat.name)}
                onClick={() => handleChatClick(chat)}
                sx={{ py: 2, px: { xs: 1.5, md: 3 } }}
              >
                <ListItemAvatar>
                  <Badge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} variant="dot" color="success">
                    <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', fontWeight: 700 }}>
                      {chat.name[0]}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText 
                  primary={chat.name} 
                  secondary={chat.message}
                  sx={{ display: { xs: 'none', md: 'block' } }}
                  primaryTypographyProps={{ fontWeight: 700, noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true, fontSize: '0.75rem' }}
                />
                <Typography variant="caption" sx={{ display: { xs: 'none', md: 'block' }, ml: 1, color: 'text.secondary' }}>
                  {moment(chat.time).format('h:mm A')}
                </Typography>
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* Main Chat Area */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', bgcolor: alpha(theme.palette.background.default, 0.5) }}>
          {/* Top Bar */}
          <Paper elevation={0} sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', borderRadius: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} color="text.secondary">To:</Typography>
              <Autocomplete
                multiple
                fullWidth
                options={recipientList}
                getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                value={selectedRecipients}
                onChange={(event, newValue) => setSelectedRecipients(newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" color="primary" label={`${option.first_name} ${option.last_name}`} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} variant="standard" placeholder="Find members..." InputProps={{ ...params.InputProps, disableUnderline: true }} />
                )}
                sx={{ '& .MuiAutocomplete-inputRoot': { p: '4px 8px' } }}
              />
            </Box>
          </Paper>

          {/* Messages List */}
          <Box ref={scrollRef} sx={{ flexGrow: 1, p: 3, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!selectedChat ? (
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', opacity: 0.5 }}>
                <Forum sx={{ fontSize: '4rem', mb: 2 }} />
                <Typography variant="h6">Select a conversation to start messaging</Typography>
              </Box>
            ) : (
              messages
                .filter(m => !m.is_deleted && (m.sender === selectedChat || m.receiver === selectedChat || m.receiver.includes(selectedChat)))
                .map((msg, idx) => {
                  const isSender = user && msg.sender === `${user.first_name} ${user.last_name}`;
                  return (
                    <Box key={idx} sx={{ display: 'flex', justifyContent: isSender ? 'flex-end' : 'flex-start', mb: 1 }}>
                      {!isSender && (
                        <Avatar sx={{ width: 32, height: 32, mr: 1, mt: 1, fontSize: '0.75rem' }}>{msg.sender[0]}</Avatar>
                      )}
                      <Box sx={{ maxWidth: '70%', position: 'relative', '&:hover .msg-actions': { opacity: 1 } }}>
                        <Paper sx={{ 
                          p: 2, 
                          borderRadius: isSender ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                          bgcolor: isSender ? 'primary.main' : 'background.paper',
                          color: isSender ? 'primary.contrastText' : 'text.primary',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        }}>
                          <Typography variant="body2">{msg.content}</Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7, textAlign: 'right', fontSize: '0.65rem' }}>
                            {moment(msg.timestamp).format('h:mm A')}
                          </Typography>
                        </Paper>
                        
                        <Box 
                          className="msg-actions" 
                          sx={{ 
                            position: 'absolute', 
                            top: -10, 
                            [isSender ? 'left' : 'right']: -30, 
                            opacity: 0, 
                            transition: '0.2s',
                          }}
                        >
                          <IconButton size="small" onClick={(e) => handleMessageMenuOpen(e, msg)}>
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  );
                })
            )}
          </Box>

          {/* Input Area */}
          <Box sx={{ p: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
              <Box sx={{ position: 'relative' }}>
                <IconButton onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                  <InsertEmoticon />
                </IconButton>
                {showEmojiPicker && (
                  <Box sx={{ position: 'absolute', bottom: '100%', left: 0, mb: 1, zIndex: 1000 }}>
                    <EmojiPicker onEmojiClick={addEmoji} />
                  </Box>
                )}
              </Box>
              
              <IconButton onClick={() => setShowAttachmentDialog(true)}>
                <AttachFile />
              </IconButton>

              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Write a message..."
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: '24px',
                    bgcolor: 'action.hover',
                    '& fieldset': { border: 'none' }
                  } 
                }}
              />

              <IconButton 
                color="primary" 
                onClick={handleSendMessage}
                disabled={!messageInput.trim() || selectedRecipients.length === 0}
                sx={{ 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' },
                  '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
                }}
              >
                <Send />
              </IconButton>
            </Box>
          </Box>
        </Box>
        
        {/* Message Actions Menu */}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMessageMenuClose}>
          <MenuItem onClick={handleMessageMenuClose}><Forward sx={{ mr: 1, fontSize: 18 }} /> Forward</MenuItem>
          <MenuItem onClick={handleMessageMenuClose}><Share sx={{ mr: 1, fontSize: 18 }} /> Share</MenuItem>
          <Divider sx={{ my: 1 }} />
          <MenuItem onClick={handleDeleteMessage} sx={{ color: 'error.main' }}><Delete sx={{ mr: 1, fontSize: 18 }} /> Delete</MenuItem>
        </Menu>

        {/* Attachment Dialog */}
        <Dialog open={showAttachmentDialog} onClose={() => setShowAttachmentDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Upload Attachment</DialogTitle>
          <DialogContent>
            <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Button variant="outlined" component="label" fullWidth sx={{ py: 4, borderStyle: 'dashed' }}>
                Select File
                <input type="file" hidden onChange={(e) => setFileInput(e.target.files[0])} />
              </Button>
              {fileInput && <Typography variant="caption" sx={{ mt: 1 }}>{fileInput.name}</Typography>}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAttachmentDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={() => setShowAttachmentDialog(false)}>Attach</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
